import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { FileSpreadsheet, RefreshCw } from "lucide-react";
import AxiosDB from "@/Config/AxiosDB";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COLUMNS = [
  { key: "tipo_comprobante", label: "Tipo Comp.", text: true },
  { key: "cantidad", label: "Cantidad", int: true },
  { key: "subtotal", label: "Subtotal" },
  { key: "itbis", label: "ITBIS" },
  { key: "monto", label: "Monto" },
  { key: "total_itbis", label: "Total ITBIS" },
  { key: "total", label: "Total" },
  { key: "total_exento", label: "Total Exento" },
  { key: "total_gravado", label: "Total Gravado" },
  { key: "gravado_18", label: "Gravado 18%" },
  { key: "gravado_16", label: "Gravado 16%" },
  { key: "efectivo", label: "Efectivo" },
  { key: "cheque", label: "Cheque/Transf." },
  { key: "tarjeta", label: "Tarjeta" },
  { key: "venta_credito", label: "Venta Crédito" },
  { key: "permuta", label: "Permuta" },
  { key: "nota_credito", label: "Nota Crédito" },
  { key: "otra_forma_pago", label: "Otra Forma Pago" },
];

export default function Formulario607({ declaracionId, onReporte }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!declaracionId) return;
    setLoading(true);
    try {
      const res = await AxiosDB.get(`${API}/declaraciones-it1/${declaracionId}/reporte-ventas`);
      setData(res.data);
      if (onReporte) onReporte(res.data.filas || []);
    } catch (e) {
      toast.error("Error al cargar el reporte de ventas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [declaracionId]);

  const filas = data?.filas || [];

  return (
    <div className="py-2" data-testid="form-607">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-700">Reporte de Ventas por Tipo de Comprobante</h3>
          {data?.usando_todos && (
            <p className="text-xs text-amber-600">Mostrando todos los comprobantes emitidos (el período no tiene registros).</p>
          )}
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800" data-testid="recargar-607">
          <RefreshCw className="w-4 h-4" /> Recargar
        </button>
      </div>

      {loading ? (
        <p className="text-center text-slate-500 py-8">Cargando...</p>
      ) : filas.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border border-dashed rounded-lg" data-testid="form-607-empty">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p>No hay ventas registradas para este período.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="reporte-607-table">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-slate-100 text-slate-700">
                {COLUMNS.map((c) => (
                  <th key={c.key} className="px-3 py-2 text-left whitespace-nowrap font-semibold border-b border-slate-200">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((row, i) => (
                <tr key={row.tipo_comprobante} className={i % 2 ? "bg-white" : "bg-slate-50"} data-testid={`venta-row-${row.tipo_comprobante}`}>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.text ? "font-semibold text-slate-800" : "text-right font-mono"}`}>
                      {c.text ? row[c.key] : c.int ? row[c.key] : fmt(row[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50 font-bold text-slate-800" data-testid="reporte-607-totales">
                <td className="px-3 py-2">TOTAL</td>
                {COLUMNS.slice(1).map((c) => (
                  <td key={c.key} className="px-3 py-2 text-right font-mono">
                    {c.int ? (data?.totales?.[c.key] || 0) : fmt(data?.totales?.[c.key])}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
